import React from "react";
import { MODULES, DEFAULT_DOC_TEMPLATE, DOC_BLOCK_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, GripVertical, Eye, EyeOff, Sparkles } from "lucide-react";
import { Reorder } from "framer-motion";
import { toast } from "sonner";
import PageLibrary from "@/components/PageLibrary";
import DocumentPreview from "@/components/DocumentPreview";
import FileField from "@/components/stock/FileField";

const ORANGE = "rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]";
const DARK_FIELD = "mt-1 border-zinc-800 bg-zinc-950";
const HAS_TITLE = ["client", "payment", "notes", "legal", "signature"];
const HAS_TEXT = ["legal", "footer"];
const SAMPLE_CONTACT = { name: "Marie Dupont", email: "marie@exemple.fr" };
const SAMPLE_QUOTE = {
  number: "2026-001", is_invoice: false, created_at: new Date().toISOString(),
  tax_rate: 20, deposit_percent: 30, balance_days_before: 7,
  notes: "Merci pour votre confiance.",
  lines: [
    { name: "Prestation DJ", description: "6h de mix", qty: 1, price: 800 },
    { name: "Pack lumière", qty: 1, price: 350 },
  ],
  subtotal: 1150, tax: 230, total: 1380, deposit: 414, balance: 966,
};

function ModulesUpsell({ limit, onSeePlans }) {
  return (
    <div data-testid="modules-upsell" className="mt-5 flex flex-col gap-3 rounded-2xl border border-[#FF6A00]/40 bg-gradient-to-br from-[#FF6A00]/15 to-[#FFD500]/10 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FF6A00]/20"><Sparkles className="h-5 w-5 text-[#FF6A00]" /></div>
        <div>
          <p className="font-display text-base font-semibold text-white">Limite de modules atteinte</p>
          <p className="mt-0.5 text-sm text-zinc-400">Votre formule autorise {limit} module{limit > 1 ? "s" : ""}. Passez à une formule supérieure pour en activer davantage.</p>
        </div>
      </div>
      <Button data-testid="modules-upsell-cta" onClick={onSeePlans} className={`shrink-0 ${ORANGE}`}><Sparkles className="mr-1.5 h-4 w-4" />Voir les formules</Button>
    </div>
  );
}

export function ModulesTab({ form, setForm, access, onSave, onSeePlans }) {
  const limit = access?.modules_limit;
  const allowed = access?.modules_allowed || null;
  const soonList = access?.modules_soon || [];
  const mods = (form.modules || []).filter((m) => !soonList.includes(m));
  const toggle = (k) => {
    if (soonList.includes(k)) { toast("Ce module sera disponible bientôt."); return; }
    if (mods.includes(k)) { setForm({ ...form, modules: (form.modules || []).filter((m) => m !== k) }); return; }
    if (allowed && !allowed.includes(k)) { toast.error("Ce module n'est pas inclus dans votre abonnement."); return; }
    if (limit != null && mods.length >= limit) { toast.error(`Votre abonnement autorise ${limit} module(s) maximum.`); return; }
    setForm({ ...form, modules: [...(form.modules || []), k] });
  };
  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-zinc-500">Activez les modules métiers correspondant à votre activité. Ils apparaissent dans la navigation.</p>
        {limit != null && <span data-testid="modules-limit" className="shrink-0 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-400">{mods.length} / {limit} modules</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {MODULES.map((m) => {
          const active = mods.includes(m.key);
          const soon = soonList.includes(m.key);
          const locked = soon || (allowed && !allowed.includes(m.key));
          return (
            <button key={m.key} data-testid={`toggle-module-${m.key}`} disabled={locked} onClick={() => toggle(m.key)}
              className={`relative flex flex-col items-center gap-2 rounded-2xl border p-4 text-center text-sm transition-all ${soon ? "cursor-not-allowed border-dashed border-zinc-700 bg-zinc-900/40 text-zinc-500" : locked ? "cursor-not-allowed border-zinc-800 bg-zinc-900/40 text-zinc-600 opacity-60" : active ? "border-[#FF6A00] bg-[#FF6A00]/10 text-white" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
              <m.icon className={`h-6 w-6 ${active ? "text-[#FF6A00]" : ""}`} strokeWidth={1.6} />
              {m.label}
              {soon ? <span data-testid={`module-soon-${m.key}`} className="soon-badge rounded-full px-2 py-0.5 text-[10px] font-medium">Disponible bientôt</span> : locked ? <span className="text-[10px] text-zinc-500">Non inclus</span> : active && <span className="text-[10px] text-[#FF6A00]">● Activé</span>}
            </button>
          );
        })}
      </div>
      {limit != null && MODULES.length > limit && mods.length >= limit && <ModulesUpsell limit={limit} onSeePlans={onSeePlans} />}
      <Button data-testid="save-modules" onClick={onSave} className={`mt-5 ${ORANGE}`}><Check className="mr-1 h-4 w-4" />Appliquer</Button>
    </>
  );
}

export function CommercialTab({ form, setForm, onSave }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
        <div className="grid grid-cols-2 gap-3 md:col-span-2">
          <div><Label className="text-zinc-400">Acompte par défaut (%)</Label><Input type="number" className={DARK_FIELD} value={form.deposit_percent ?? 30} onChange={set("deposit_percent")} /></div>
          <div><Label className="text-zinc-400">Relance solde (jours avant l'événement)</Label><Input data-testid="balance-days-before" type="number" className={DARK_FIELD} value={form.balance_days_before ?? 7} onChange={set("balance_days_before")} /></div>
        </div>
        <div><Label className="text-zinc-400">Relance validation design (jours)</Label><Input data-testid="pb-reminder-days" type="number" min="0" className={DARK_FIELD} value={form.pb_reminder_days ?? 3} onChange={set("pb_reminder_days")} /><p className="mt-1 text-xs text-zinc-500">Rappel automatique au client si le design photobooth n'est pas validé après X jours (0 = désactivé).</p></div>
        <div><Label className="text-zinc-400">Politique d'annulation</Label><Textarea rows={3} className={DARK_FIELD} value={form.cancellation_policy || ""} onChange={set("cancellation_policy")} /></div>
        <div><Label className="text-zinc-400">Mentions légales par défaut</Label><Textarea data-testid="org-legal" rows={2} className={DARK_FIELD} value={form.legal_mentions || ""} onChange={set("legal_mentions")} /></div>
        <Button data-testid="save-commercial" onClick={onSave} className={ORANGE}><Check className="mr-1 h-4 w-4" />Enregistrer</Button>
      </div>
    </div>
  );
}

function PageImageField({ label, hint, testid, value, onChange }) {
  return (
    <div className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
      <Label className="text-zinc-400">{label}</Label>
      <p className="mt-0.5 text-xs text-zinc-500">{hint}</p>
      <FileField testid={testid} kind="page" large accept="image/png,image/jpeg,image/webp" value={value} onChange={(v) => onChange(v || null)} />
    </div>
  );
}

function BlockItem({ b, i, onToggle, onEdit }) {
  return (
    <Reorder.Item value={b} data-testid={`block-item-${b.type}`} className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
      <div className="flex items-center gap-3">
        <GripVertical className="h-4 w-4 cursor-grab text-zinc-600" />
        <span className="flex-1 text-sm font-medium text-white">{DOC_BLOCK_LABELS[b.type]}</span>
        <button data-testid={`block-toggle-${b.type}`} onClick={() => onToggle(i)} className="text-zinc-500 hover:text-white">
          {b.visible !== false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-zinc-700" />}
        </button>
      </div>
      {b.visible !== false && (HAS_TITLE.includes(b.type) || HAS_TEXT.includes(b.type)) && (
        <div className="mt-2 space-y-2 pl-7">
          {HAS_TITLE.includes(b.type) && <Input data-testid={`block-title-${b.type}`} placeholder="Titre du bloc" className="h-8 border-zinc-800 bg-zinc-950 text-xs" value={b.title || ""} onChange={(e) => onEdit(i, "title", e.target.value)} />}
          {HAS_TEXT.includes(b.type) && <Textarea data-testid={`block-text-${b.type}`} placeholder="Texte" rows={2} className="border-zinc-800 bg-zinc-950 text-xs" value={b.text || ""} onChange={(e) => onEdit(i, "text", e.target.value)} />}
        </div>
      )}
    </Reorder.Item>
  );
}

export function DocumentsTab({ form, setForm, onSave }) {
  const tpl = form.document_template && form.document_template.blocks ? form.document_template : DEFAULT_DOC_TEMPLATE;
  const setTpl = (patch) => setForm({ ...form, document_template: { ...tpl, ...patch } });
  const setBlocks = (blocks) => setTpl({ blocks });
  const toggleBlock = (i) => setBlocks(tpl.blocks.map((b, idx) => (idx === i ? { ...b, visible: b.visible === false } : b)));
  const editBlock = (i, k, v) => setBlocks(tpl.blocks.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <p className="mb-3 text-sm text-zinc-500">Glissez-déposez pour réordonner les blocs de vos devis et factures. Masquez ceux dont vous n'avez pas besoin et personnalisez les titres.</p>
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-3">
          <Label className="text-zinc-400">Couleur du document</Label>
          <input type="color" data-testid="doc-primary-color" value={tpl.primary_color || form.brand_color || "#FF6A00"} onChange={(e) => setTpl({ primary_color: e.target.value })} className="h-9 w-14 rounded-lg border border-zinc-800 bg-zinc-950" />
          {tpl.primary_color && <button onClick={() => setTpl({ primary_color: null })} className="text-xs text-zinc-500 hover:text-white">Réinitialiser</button>}
        </div>
        <PageImageField label="Page d'ouverture (image A4)" hint="Affichée en 1re page de vos devis (ex : belle photo + logo)." testid="opening-image-input" alt="ouverture" value={tpl.opening_image} onChange={(v) => setTpl({ opening_image: v })} />
        <PageImageField label="Page de clôture (image A4)" hint="Affichée en dernière page du devis (ex : conditions, remerciements, coordonnées)." testid="closing-image-input" alt="clôture" value={tpl.closing_image} onChange={(v) => setTpl({ closing_image: v })} />
        <PageLibrary />
        <Reorder.Group axis="y" values={tpl.blocks} onReorder={setBlocks} className="space-y-2">
          {tpl.blocks.map((b, i) => <BlockItem key={b.type} b={b} i={i} onToggle={toggleBlock} onEdit={editBlock} />)}
        </Reorder.Group>
        <Button data-testid="save-documents" onClick={onSave} className={`mt-4 ${ORANGE}`}><Check className="mr-1 h-4 w-4" />Enregistrer le modèle</Button>
      </div>
      <div className="lg:sticky lg:top-24 lg:self-start">
        <p className="mb-2 text-xs text-zinc-500">Aperçu (données d'exemple)</p>
        <DocumentPreview template={tpl} org={form} quote={SAMPLE_QUOTE} contact={SAMPLE_CONTACT} />
      </div>
    </div>
  );
}
